import {
    CombinedTimeSeriesOverview,
    StatDisplay,
} from 'components/TimeSeriesOverview';
import {
    formatSeriesData,
    getSeriesColors,
} from './Charts/MultiPredictionTimeSeriesChart/utils';
import { forwardRef, memo } from 'react';
import Chart from './Charts/MultiPredictionTimeSeriesChart';
import { TIKTOK_METADATA_BY_KEY } from 'utils/metrics';
import TikTokTimeSeriesNoDataMessage from 'components/TikTokTimeSeriesNoDataMessage';
import TimeSeriesKey from './TimeSeriesKey';
import Tooltip from './Charts/MultiPredictionTimeSeriesChart/components/Tooltip';
import { timeseries as copy } from '@/copy';
import dayjs from 'dayjs';
import { getAverageTimeSeries } from './utils/index';
import { getXAxisTickFns } from './Charts/TikTokSearchTimeSeries/utils';
import isEqual from 'react-fast-compare';
import styled from 'styled-components';
import weekOfYear from 'dayjs/plugin/weekOfYear';

dayjs.extend(weekOfYear);

const formatAndParseTime = (v) => {
    const s = dayjs(v, 'YYYY-MM-DD').format('MMM D, YYYY');
    return s;
};

const TikTokTooltip = forwardRef(
    (
        {
            formatAndParseTime: formatAndParseTimeProp = formatAndParseTime,
            metricLabel = 'TikTok Views',
            ...props
        },
        ref,
    ) => (
        <Tooltip
            {...props}
            formatAndParseTime={formatAndParseTimeProp}
            metricLabel={metricLabel}
            ref={ref}
        />
    ),
);

const TikTokTimeSeriesWrapper = styled.div`
    display: flex;
    flex-direction: column;
`;

const defaultMetricsList = [
    TIKTOK_METADATA_BY_KEY.non_cum_YoY_rolling,
    TIKTOK_METADATA_BY_KEY.non_cum_QoQ_rolling,
    TIKTOK_METADATA_BY_KEY.non_cum_MoM_rolling,
    TIKTOK_METADATA_BY_KEY.non_cum_WoW,
];

const TikTokTimeSeries = memo(
    forwardRef(
        (
            {
                data,
                week,
                year,
                isLoading,
                noDataMessage = <TikTokTimeSeriesNoDataMessage />,
                Wrapper = TikTokTimeSeriesWrapper,
                Tooltip = TikTokTooltip,
                metricsList = defaultMetricsList,
                metrics,
                defaultMetric = TIKTOK_METADATA_BY_KEY.non_cum_YoY_rolling,
                viewSumTimeSeries,
                chartHeight = 250,
                showStatDisplay = false,
                showDataSourceBar = true,
                customCss,
                ...props
            },
            ref,
        ) => {
            const sortedData =
                data && data.length
                    ? data
                          .slice()
                          .sort(
                              (a, b) =>
                                  b.timeSeries.length - a.timeSeries.length,
                          )
                    : data;
            // need to have longest timeseries first for correctly establishing xAxis labels
            const { xAxisTickFormat, xAxisTickValues } =
                getXAxisTickFns(sortedData);
            let maybeSumData = data;
            if (viewSumTimeSeries && data && data.length) {
                const { accessors } = formatSeriesData(data);
                maybeSumData = [
                    {
                        id: 'combined',
                        timeSeries: getAverageTimeSeries(data, accessors),
                        color: getSeriesColors(0),
                        name: 'Combined',
                    },
                ];
            }
            return (
                <div css="margin-bottom: 12px;" ref={ref}>
                    <Chart
                        chartHeight={chartHeight}
                        data={maybeSumData}
                        isLoading={isLoading}
                        showPlaceholder={!data || data.length < 1}
                        placeholderMessage={
                            isLoading ? copy.loadingTikTok : noDataMessage
                        }
                        predictionStrokeDasharray={false}
                        Wrapper={Wrapper}
                        xAxisTickFormat={xAxisTickFormat}
                        xAxisTickValues={xAxisTickValues}
                        Tooltip={Tooltip}
                    >
                        <div
                            css={`
                                display: flex;
                                align-items: flex-start;
                                justify-content: space-between;
                            `}
                        >
                            {showStatDisplay && (
                                <StatDisplay
                                    non_cum_avg_weekly_views={
                                        metrics.non_cum_avg_weekly_views
                                    }
                                    week={week}
                                    year={year}
                                    isLoading={isLoading}
                                    valueKey="non_cum_avg_weekly_views"
                                    tooltipKey="non_cum_avg_weekly_views"
                                    contentKey="dataTableViews"
                                    labelPrefix="Avg. Weekly"
                                    labelSuffix="Views"
                                />
                            )}
                            <CombinedTimeSeriesOverview
                                isLoading={isLoading}
                                metrics={
                                    metrics
                                        ? [
                                              {
                                                  metrics: metrics || {},
                                                  metricsList,
                                                  defaultMetric,
                                                  week,
                                                  year,
                                              },
                                          ]
                                        : null
                                }
                                showDataSource={false}
                                showDataSourceBar={showDataSourceBar}
                                customCss={customCss}
                                wrapperCss={'width: 74%;'}
                            />
                        </div>
                    </Chart>
                    {!viewSumTimeSeries ? (
                        <TimeSeriesKey
                            data={data}
                            style={{ display: 'none', marginTop: '1em' }}
                            className="image-download-visible"
                        />
                    ) : null}
                </div>
            );
        },
    ),
    isEqual,
);

export default TikTokTimeSeries;
